import React, { useState, useEffect } from 'react';
import { getInstallInfo, isServiceWorkerActive, sendMessageToSW } from '../registerServiceWorker';
import './PWAStatus.css';

interface PWAStatusProps {
  showDetails?: boolean;
  onStatusChange?: (status: any) => void;
}

export const PWAStatus: React.FC<PWAStatusProps> = ({ 
	showDetails = false,
	onStatusChange 
}) => {
	const [status, setStatus] = useState<any>(null);
	const [isActive, setIsActive] = useState<boolean>(false);
	const [cacheInfo, setCacheInfo] = useState<any>(null);

	useEffect(() => {
		const updateStatus = () => {
			const info = getInstallInfo();
			setStatus(info);
			onStatusChange?.(info);
      
			// Проверяем активность Service Worker
			isServiceWorkerActive().then(active => {
				setIsActive(active);
			});
		};

		updateStatus();
    
		// Обновляем статус при изменении сети
		window.addEventListener('online', updateStatus);
		window.addEventListener('offline', updateStatus);
    
		// Периодически обновляем статус
		const interval = setInterval(updateStatus, 30000);
    
		return () => {
			window.removeEventListener('online', updateStatus);
			window.removeEventListener('offline', updateStatus);
			clearInterval(interval);
		};
	}, [onStatusChange]);

	const getCacheInfo = async () => {
		try {
			await sendMessageToSW({ type: 'GET_CACHE_INFO' });
		} catch (error) {
			console.error('Ошибка получения информации о кэше:', error);
		}
	};

	const clearCache = async () => {
		try {
			await sendMessageToSW({ type: 'CLEAR_CACHE' });
			alert('Кэш очищен');
		} catch (error) {
			console.error('Ошибка очистки кэша:', error);
		}
	};

	if (!status) return null;

	if (!showDetails) {
		return (
			<div className="pwa-status pwa-status--compact">
				<div className="pwa-status__indicator">
					<div className={`pwa-status__dot ${
						status.isOnline ? 'pwa-status__dot--online' : 'pwa-status__dot--offline'
					}`} />
					<span className="pwa-status__text">
						{status.isOnline ? 'Онлайн' : 'Оффлайн'}
					</span>
				</div>
				{status.isInstalled && (
					<div className="pwa-status__badge" title="Установлено как PWA">
            📱
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="pwa-status pwa-status--detailed">
			<div className="pwa-status__header">
				<h3 className="pwa-status__title">Статус приложения</h3>
			</div>
      
			<div className="pwa-status__grid">
				<div className="pwa-status__item">
					<span className="pwa-status__label">Режим:</span>
					<span className="pwa-status__value">
						{status.isInstalled ? '📱 PWA' : '🌐 Браузер'}
					</span>
				</div>
        
				<div className="pwa-status__item">
					<span className="pwa-status__label">Сеть:</span>
					<span className={`pwa-status__value ${
						status.isOnline ? 'pwa-status__value--online' : 'pwa-status__value--offline'
					}`}>
						{status.isOnline ? '✅ Онлайн' : '❌ Оффлайн'}
					</span>
				</div>
        
				<div className="pwa-status__item">
					<span className="pwa-status__label">Service Worker:</span>
					<span className={`pwa-status__value ${
						isActive ? 'pwa-status__value--active' : 'pwa-status__value--inactive'
					}`}>
						{isActive ? '✅ Активен' : '❌ Неактивен'}
					</span>
				</div>
        
				<div className="pwa-status__item">
					<span className="pwa-status__label">Установка:</span>
					<span className="pwa-status__value">
						{status.supportsPWA ? '✅ Поддерживается' : '❌ Не поддерживается'}
					</span>
				</div>
			</div>
      
			{status.supportsServiceWorker && (
				<div className="pwa-status__actions">
					<button 
						className="pwa-status__button"
						onClick={getCacheInfo}
						title="Получить информацию о кэше"
					>
            Инфо о кэше
					</button>
					<button 
						className="pwa-status__button pwa-status__button--danger"
						onClick={clearCache}
						title="Очистить кэш"
					>
            Очистить кэш
					</button>
					<button 
						className="pwa-status__button"
						onClick={() => window.location.reload()}
						title="Перезагрузить приложение"
					>
            Перезагрузить
					</button>
				</div>
			)}
      
			{cacheInfo && (
				<div className="pwa-status__cache-info">
					<h4>Информация о кэше:</h4>
					<p>Записей: {cacheInfo.count}</p>
					<ul>
						{cacheInfo.urls.slice(0, 5).map((url: string, index: number) => (
							<li key={index} title={url}>
								{url.substring(url.lastIndexOf('/') + 1)}
							</li>
						))}
						{cacheInfo.count > 5 && <li>... и ещё {cacheInfo.count - 5}</li>}
					</ul>
				</div>
			)}
		</div>
	);
};

export default PWAStatus;

